import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getDiscrepancy, listDiscrepancies } from "@/api/discrepancies";
import { updateFlagStatus } from "@/api/audit";
import { ActionPanel } from "@/components/reconciliation/ActionPanel";
import { DismissModal } from "@/components/reconciliation/DismissModal";
import { InvoiceViewer } from "@/components/reconciliation/InvoiceViewer";
import { PushTaskModal } from "@/components/reconciliation/PushTaskModal";
import { SoapViewer } from "@/components/reconciliation/SoapViewer";
import { isTyping, neighbours } from "@/components/reconciliation/queue";
import { QueryError } from "@/components/ui/QueryError";
import { useTenant } from "@/hooks/useTenant";
import { useToasts } from "@/hooks/useToast";
import { asId, epochToDate, highlightTerms } from "@/utils/ezyvetModels";
import { ezyvetRecordUrl } from "@/utils/ezyvetLinks";
import type { ApiError } from "@/types";
import type { Discrepancy, Page } from "@/types/ezyvet";

type ModalKind = "push" | "dismiss" | null;

const SHORTCUTS: [string, string][] = [
  ["j / k", "Next / previous pending discrepancy"],
  ["t", "Push task to ezyVet"],
  ["d", "Dismiss with a reason"],
  ["a", "Mark added to invoice"],
  ["n", "Jump to next highlighted mention"],
  ["↑ ↓ Home End", "Move through invoice lines"],
  ["?", "Show or hide shortcuts"],
];

/**
 * /reconciliation/:discrepancyId — the ezyVet consult's SOAP notes beside its
 * invoice, with the flagged item highlighted in both, and keyboard-driven
 * triage: push a task to ezyVet, mark it added, or dismiss it with a reason.
 */
export function ReconciliationWorkbench() {
  const { discrepancyId = "" } = useParams<{ discrepancyId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const push = useToasts((s) => s.push);
  const { ezyvetSite } = useTenant();
  const [modal, setModal] = useState<ModalKind>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [jumpSignal, setJumpSignal] = useState(0);

  const discrepancy = useQuery<Discrepancy, ApiError>({
    queryKey: ["discrepancies", "detail", discrepancyId],
    queryFn: () => getDiscrepancy(discrepancyId),
    enabled: Boolean(discrepancyId),
  });
  const queue = useQuery<Page<Discrepancy>, ApiError>({
    queryKey: ["discrepancies", "queue"],
    queryFn: () => listDiscrepancies({ status: "pending" }),
    staleTime: 15_000,
  });

  const d = discrepancy.data;
  const { index, prev, next } = useMemo(() => neighbours(queue.data?.items, discrepancyId), [queue.data, discrepancyId]);
  const go = (id: string | undefined) => id && navigate(`/reconciliation/${id}`);

  const afterResolve = (message: string) => {
    push(message, "success");
    qc.invalidateQueries({ queryKey: ["discrepancies"] });
    if (d) {
      qc.invalidateQueries({ queryKey: ["audit", "report", d.appointment_id] });
      qc.invalidateQueries({ queryKey: ["appointments"] });
    }
    // Keep triaging: move on to the next pending item, if any
    if (next) go(next);
  };

  const markAdded = useMutation<void, ApiError>({
    mutationFn: () => updateFlagStatus(discrepancyId, "added"),
    onSuccess: () => afterResolve("Marked as added to the invoice."),
    onError: (err) => push(err.message ?? "Update failed", "error"),
  });

  // Keyboard triage. Read the latest state through a ref so the listener is attached once.
  const keys = useRef({ d, modal, prev, next, busy: markAdded.isPending, go, add: markAdded.mutate });
  keys.current = { d, modal, prev, next, busy: markAdded.isPending, go, add: markAdded.mutate };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const s = keys.current;
      if (s.modal || e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
      const open = s.d?.status === "pending";
      const act: Record<string, (() => void) | undefined> = {
        j: () => s.go(s.next),
        k: () => s.go(s.prev),
        n: () => setJumpSignal((n) => n + 1),
        "?": () => setShowHelp((h) => !h),
        t: open && s.d?.external_source === "ezyvet" && !s.d.pms_task_id ? () => setModal("push") : undefined,
        d: open ? () => setModal("dismiss") : undefined,
        a: open && !s.busy ? () => s.add() : undefined,
      };
      const run = act[e.key];
      if (run) {
        e.preventDefault();
        run();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const terms = useMemo(() => (d ? highlightTerms(d.event_description, d.detected_terms) : []), [d]);

  if (discrepancy.isLoading) {
    return (
      <div className="card loading">
        <div className="spinner" />
        <div>Loading discrepancy…</div>
      </div>
    );
  }
  if (discrepancy.error || !d) {
    // A re-audit (e.g. ezyVet changed the invoice) replaces pending flags, so an open
    // workbench can find its flag gone: say so and offer the next one
    if (discrepancy.error?.status === 404 && queue.data) {
      return (
        <div className="card" role="status">
          <h2 style={{ marginTop: 0 }}>This discrepancy is no longer open</h2>
          <p className="muted">
            The consult was re-audited, probably because it changed in ezyVet, or someone else resolved it.
          </p>
          <div className="row">
            {next ? (
              <button type="button" className="btn btn-primary" onClick={() => go(next)}>
                Next pending discrepancy →
              </button>
            ) : (
              <span className="muted">No pending discrepancies left.</span>
            )}
            <Link to="/appointments" className="btn">
              Appointments
            </Link>
          </div>
        </div>
      );
    }
    return discrepancy.error ? (
      <QueryError
        error={discrepancy.error}
        what="this discrepancy"
        onRetry={() => discrepancy.refetch()}
        notFound="This discrepancy doesn't exist, or the reconciliation API isn't available on this server yet."
      />
    ) : null;
  }

  const consultId = asId(d.consult?.id);
  const consultUrl = consultId ? ezyvetRecordUrl(ezyvetSite, "consult", consultId) : null;
  const date = d.appointment_date ? new Date(d.appointment_date) : epochToDate(d.consult?.date);

  return (
    <div className="workbench">
      <header className="workbench-header">
        <div>
          <Link to={`/appointments/${d.appointment_id}`} className="muted">
            ← Appointment audit
          </Link>
          <h1 style={{ margin: "4px 0 0" }}>{d.patient_name || "Unnamed patient"}</h1>
          <p className="page-sub" style={{ margin: 0 }}>
            {date ? date.toLocaleDateString() : "No date"}
            {d.vet_name && ` · ${d.vet_name}`}
            {consultId &&
              (consultUrl ? (
                <>
                  {" · "}
                  <a href={consultUrl} target="_blank" rel="noopener noreferrer">
                    ezyVet consult #{consultId} ↗
                  </a>
                </>
              ) : (
                ` · ezyVet consult #${consultId}`
              ))}
          </p>
        </div>
        <nav className="row" aria-label="Pending discrepancies">
          <button type="button" className="btn btn-sm" disabled={!prev} onClick={() => go(prev)} aria-keyshortcuts="k">
            ← Prev <kbd>k</kbd>
          </button>
          <span className="muted mono" aria-live="polite">
            {queue.data ? (index >= 0 ? `${index + 1} / ${queue.data.total} pending` : `${queue.data.total} pending`) : ""}
          </span>
          <button type="button" className="btn btn-sm" disabled={!next} onClick={() => go(next)} aria-keyshortcuts="j">
            Next <kbd>j</kbd> →
          </button>
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => setShowHelp((h) => !h)}
            aria-expanded={showHelp}
            aria-controls="shortcut-help"
            aria-keyshortcuts="?"
          >
            <kbd>?</kbd>
          </button>
        </nav>
      </header>

      {showHelp && (
        <div id="shortcut-help" className="card shortcut-help">
          <dl>
            {SHORTCUTS.map(([k, label]) => (
              <div key={k}>
                <dt>
                  <kbd>{k}</kbd>
                </dt>
                <dd>{label}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <div className="workbench-grid">
        <SoapViewer consult={d.consult} terms={terms} jumpSignal={jumpSignal} />
        <InvoiceViewer
          lines={d.invoice_lines}
          invoice={d.invoice}
          matchedLineId={d.matched_line_id}
          missing={d.billed ? null : { description: d.event_description, estimatedValue: d.estimated_value_usd }}
        />
        <ActionPanel
          discrepancy={d}
          busy={markAdded.isPending}
          onPushTask={() => setModal("push")}
          onDismiss={() => setModal("dismiss")}
          onMarkAdded={() => markAdded.mutate()}
        />
      </div>

      <PushTaskModal
        discrepancy={d}
        open={modal === "push"}
        onClose={() => setModal(null)}
        onPushed={(res) => {
          push(`Task #${res.task_id} created in ezyVet.`, "success");
          qc.invalidateQueries({ queryKey: ["discrepancies"] });
        }}
      />
      <DismissModal
        discrepancy={d}
        open={modal === "dismiss"}
        onClose={() => setModal(null)}
        onDismissed={() => afterResolve("Discrepancy dismissed.")}
      />
    </div>
  );
}
